// Plates tab: licence plate -> customer registry.
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState, useMemo } from 'react';
import { useApi, putJson, delJson } from '../../api.js';

export default function PlatesPanel({ canWrite = true }) {
  // Data: the plate registry from /api/plates, plus the filter box and the add/edit pop-up.
  const { data, error, loading, refresh } = useApi('/api/plates');
  const [filter, setFilter] = useState('');
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  // Derived lists (must stay above the early returns below - see note).
  // Rules of Hooks: memoize BEFORE any conditional early return. If the
  // useMemo calls come after 'if (loading) return ...', React sees a
  // different hook count on the first render vs. the settled render and
  // starts corrupting sibling component state — including CustomersPanel's
  // modal, which was mysteriously rendering the backdrop but not the card.
  const plates = (data && data.plates) || [];
  const f = filter.toLowerCase().trim();
  const shown = useMemo(() => (
    !f ? plates : plates.filter(p =>
      (p.plate || '').toLowerCase().includes(f) ||
      (p.customer || '').toLowerCase().includes(f))
  ), [plates, f]);
  const warnStats = useMemo(() => {
    let warnCount = 0;
    let minDays = Infinity;
    for (const p of plates) {
      if (!p.warningAt) continue;
      warnCount += 1;
      if (p.daysUntilDelete != null && p.daysUntilDelete < minDays) minDays = p.daysUntilDelete;
    }
    return { warnCount, minDays };
  }, [plates]);

  if (loading) return <div className="loading">Loading…</div>;
  if (error)   return <div className="error">Error: {error}</div>;

  // Actions: save from the pop-up, delete a plate, or "keep" a stale plate.
  async function save() {
    setBusy(true);
    try { await putJson('/api/plates', edit); setEdit(null); refresh(); }
    catch (e) { alert('Save failed: ' + e.message); }
    finally { setBusy(false); }
  }
  async function remove(p) {
    if (!confirm('Delete plate ' + p.plate + '?')) return;
    await delJson('/api/plates?plate=' + encodeURIComponent(p.plate));
    refresh();
  }
  async function keep(p) {
    // Saving the plate as-is (no field changes) clears warning_at on the
    // backend (upsertPlate sets warning_at=NULL on every UPDATE). One-click
    // way for the admin to say 'yes still valid' without opening the modal.
    setBusy(true);
    try {
      await putJson('/api/plates', { plate: p.plate, tankSize: p.tankSize, customer: p.customer, comment: p.comment });
      refresh();
    } catch (e) { alert('Keep failed: ' + e.message); }
    finally { setBusy(false); }
  }

  // Screen: stale-plate warning, toolbar, plate table, add/edit pop-up.
  return (
    <div className="card" style={{ padding: '1rem' }}>
      {/* Warning banner: how many plates are flagged stale (no trucks for 12+ months) and when the next one is auto-deleted. */}
      {(() => {
        // Warn-banner stats — two scans over `plates` on every render. Fine
        // for the current registry size but memoized so it doesn't scale
        // linearly with typing in the filter box.
        const { warnCount, minDays } = warnStats;
        if (warnCount === 0) return null;
        return (
          <div style={{ padding: '6px 10px', marginBottom: 8, background: '#fef3c7',
                        border: '1px solid #fde68a', borderRadius: 6, fontSize: '0.88em', color: '#78350f' }}>
            ⚠ <strong>{warnCount}</strong> plate{warnCount === 1 ? '' : 's'} flagged as stale (no truck activity 12+ months).
            {Number.isFinite(minDays) && (
              <> Next auto-delete in <strong>{minDays}</strong> day{minDays === 1 ? '' : 's'}.</>
            )}
            {' '}Edit or click <strong>keep</strong> on a row to cancel the countdown.
          </div>
        );
      })()}
      {/* Toolbar: plate count, filter box, + Add plate. */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <strong>{plates.length}</strong> plates
        <input type="search" placeholder="filter plate or customer" value={filter}
               onChange={e => setFilter(e.target.value)} style={{ padding: '4px 8px', flex: 1, maxWidth: 280 }} />
        <button className="refresh-btn" onClick={() => setEdit({ plate: '', tankSize: 10, customer: '' })}
                style={{ background: '#16a34a' }}>+ Add plate</button>
      </div>
      {/* Plate table: first 500 matches; stale rows are yellow with a "keep" button. */}
      <table className="fs-table tidy">
        <thead><tr><th>Plate</th><th className="num">Tank</th><th>Customer</th><th>Status</th><th>Comment</th><th></th></tr></thead>
        <tbody>
          {shown.slice(0, 500).map((p, i) => {
            const isWarn = !!p.warningAt;
            const rowStyle = isWarn ? { background: '#fef3c7' } : undefined;
            return (
            <tr key={i} style={rowStyle}>
              <td><code>{p.plate}</code></td>
              <td className="num">{p.tankSize ?? '—'}</td>
              <td>{p.customer || <span style={{ color: '#aaa' }}>—</span>}</td>
              <td className="small">
                {isWarn ? (
                  <span title={'No activity for 12+ months. Auto-delete in ' + (p.daysUntilDelete ?? '?') + ' days unless kept.'}
                        style={{ color: '#92400e', fontWeight: 600 }}>
                    ⚠ stale · {p.daysUntilDelete != null ? p.daysUntilDelete + 'd left' : 'pending delete'}
                  </span>
                ) : (
                  <span style={{ color: '#16a34a' }}>active</span>
                )}
              </td>
              <td className="small">{p.comment || ''}</td>
              <td>
                {isWarn && canWrite && (
                  <button className="link" onClick={() => keep(p)} title="Mark this plate as still valid; cancels the auto-delete countdown"
                          style={{ marginRight: 8, color: '#16a34a', fontWeight: 600 }}>keep</button>
                )}
                <button className="link" onClick={() => setEdit({ ...p })}>edit</button>
                <button className="link" onClick={() => remove(p)} style={{ marginLeft: 8, color: '#dc2626' }}>delete</button>
              </td>
            </tr>
          )})}
        </tbody>
      </table>
      {/* Add / edit pop-up. */}
      {edit && (
        <div className="modal-backdrop" onClick={() => setEdit(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ minWidth: 380 }}>
            <h3>{edit._isNew ? 'Add plate' : 'Edit plate'}</h3>
            <label className="small">Plate&nbsp;<input value={edit.plate}
                   onChange={e => setEdit({ ...edit, plate: e.target.value.toUpperCase() })} /></label><br />
            <label className="small">Tank size&nbsp;<input type="number" step="0.1" value={edit.tankSize ?? ''}
                   onChange={e => setEdit({ ...edit, tankSize: e.target.value })} /></label><br />
            <label className="small">Customer&nbsp;<input value={edit.customer || ''}
                   onChange={e => setEdit({ ...edit, customer: e.target.value })} /></label><br />
            <label className="small">Comment&nbsp;<input value={edit.comment || ''}
                   onChange={e => setEdit({ ...edit, comment: e.target.value })} /></label>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
              <button className="refresh-btn" onClick={save} disabled={busy}>Save</button>
              <button className="refresh-btn" onClick={() => setEdit(null)} style={{ background: '#888' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
